import { StateMachineContext } from '#src/state-machine/context';
import {
  type TableDataViewType,
  type ChartType,
  type ViewType,
  type YearsMode,
  DefaultFilterValues,
} from '#src/types';

export function useFilters() {
  const send = StateMachineContext.useActorRef().send;
  const context = StateMachineContext.useSelector((state) => state.context);

  const handleDatasetChange = (dataset: string) => {
    send({
      type: 'CHANGE_DATASET',
      dataset: dataset as any,
    });
  };

  const handleViewChange = (view: ViewType) => {
    send({
      type: 'CHANGE_VIEW',
      viewType: view,
    });
  };

  const handleChartTypeChange = (chartType: ChartType) => {
    send({
      type: 'CHANGE_CHART_TYPE',
      chartType,
    });
  };

  const handleLevelChange = (level: string, levelCompare?: string[][]) => {
    let compare: string[][] | undefined = levelCompare ?? context.levelCompare;
    if (level === DefaultFilterValues.level) {
      compare = undefined;
    }
    send({
      type: 'CHANGE_LEVEL',
      level: level as any,
      compare,
    });
  };

  const handleYearsModeChange = (
    yearsMode: YearsMode,
    selectedYears?: number[]
  ) => {
    let years = selectedYears ?? context.selectedYears;
    if (yearsMode === 'single') {
      years = years.slice(0, 1);
    } else if (yearsMode === 'range') {
      years = years.slice(0, 2);
    }
    send({
      type: 'CHANGE_YEARS_MODE',
      yearsMode,
      selectedYears: years,
    });
  };

  const handleCategoryChange = (
    category: string,
    categoryCompare?: string[][]
  ) => {
    let compare: string[][] | undefined =
      categoryCompare ?? context.categoryCompare;
    if (category === DefaultFilterValues.category) {
      compare = undefined;
    }
    send({
      type: 'CHANGE_CATEGORY',
      category: category as any,
      compare,
    });
  };

  const handleYearsStepperPlay = () => {
    send({
      type: 'YEARS_STEPPER_PLAY',
    });
  };

  const handleContextualDatasetChange = (contextualDataset: string | null) => {
    send({
      type: 'CHANGE_CONTEXTUAL_DATASET',
      contextualDataset,
    });
  };

  const handleFundingSourceChange = (
    parentId: string,
    fundingSourceCompare?: string[][]
  ) => {
    let compare: string[][] | undefined =
      fundingSourceCompare ?? context.fundingSourceCompare;

    if (parentId === DefaultFilterValues.fundingSource) {
      compare = undefined;
    }
    send({
      type: 'CHANGE_FUNDING_SOURCE',
      parentId,
      compare,
    });
  };

  const handleTableDataViewChange = (tableDataView: string) => {
    send({
      type: 'CHANGE_TABLE_DATA_VIEW',
      tableDataView: tableDataView as TableDataViewType,
    });
  };

  const settings = context.settings;
  const datasetOptions = context.data.datasetOptions ?? [];
  const activeDataset = datasetOptions.find(
    (option) => option.id === context.dataset
  );
  const activeView = context.viewType;
  const levels =
    context.data.levelOptions != null
      ? Object.values(context.data.levelOptions)
      : [];
  const activeLevel = levels?.find((option) => option.id === context.level);

  const categories =
    context.data.categoryOptions != null
      ? Object.values(context.data.categoryOptions)
      : [];
  const activeCategory = categories?.find(
    (option) => option.id === context.category
  );

  const fundingSources =
    context.data.fundingSources != null
      ? Object.values(context.data.fundingSources)
      : [];
  const activeFundingSource = fundingSources?.find(
    (option) => option.id === context.fundingSource
  );

  const levelCompare = context.levelCompare;
  const categoryCompare = context.categoryCompare;
  const fundingSourceCompare = context.fundingSourceCompare;

  const years = context.data.yearsOptions ?? [];
  const yearsMode = context.yearsMode;
  const selectedYears = context.selectedYears;
  const contextualDatasets = context.data.contextualDatasets ?? [];
  const activeContextualDataset = context.contextualDataset;
  const tableDataView = context.tableDataView;

  return {
    settings,
    activeDataset,
    activeView,
    datasetOptions,
    levels,
    activeLevel,
    levelCompare,
    categories,
    activeCategory,
    categoryCompare,
    fundingSources,
    activeFundingSource,
    fundingSourceCompare,
    yearsMode,
    selectedYears,
    years,
    contextualDatasets,
    activeContextualDataset,
    tableDataView,
    handleContextualDatasetChange,
    handleDatasetChange,
    handleViewChange,
    handleLevelChange,
    handleYearsModeChange,
    handleCategoryChange,
    handleChartTypeChange,
    handleYearsStepperPlay,
    handleFundingSourceChange,
    handleTableDataViewChange,
  };
}
